import React, { createContext, useContext, useState, useEffect } from 'react';
import { TranslateService } from '../services/japanese';
import { loadAppConfig, saveAppConfig, loadJapaneseConfig, saveJapaneseConfig } from '../services/storage';
import { JapaneseConfig } from '../types/config';

export interface Config {
  showHiragana: boolean;
  showRomaji: boolean;
  showTraditional: boolean;
  fontSize: number;
  theme: 'light' | 'dark';
}

interface ConfigContextType {
  config: Config;
  setConfig: (config: Config) => void;
  resetConfig: () => void;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

const initialConfig: Config = {
  showHiragana: true,
  showRomaji: true,
  showTraditional: true,
  fontSize: 16,
  theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
};

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfigState] = useState<Config>(() => {
    const savedConfig = loadAppConfig();
    return savedConfig || initialConfig;
  });

  const setConfig = (newConfig: Config) => {
    setConfigState(newConfig);
    saveAppConfig(newConfig);
  };

  const resetConfig = () => {
    setConfigState(initialConfig);
    saveAppConfig(initialConfig);
  };

  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!loadAppConfig()) { // 只在没有保存的配置时跟随系统主题
        setConfig({
          ...config,
          theme: e.matches ? 'dark' : 'light'
        });
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [config]);

  // 应用主题
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', config.theme);
    document.body.style.backgroundColor =
      config.theme === 'dark' ? 'var(--bg-primary)' : 'var(--bg-secondary)';
  }, [config.theme]);

  return (
    <ConfigContext.Provider value={{ config, setConfig, resetConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

const defaultJapaneseConfig: JapaneseConfig = {
  showHiragana: true,
  showRomaji: true,
  translateService: TranslateService.Google
};

interface JapaneseConfigContextType {
  config: JapaneseConfig;
  updateConfig: (config: Partial<JapaneseConfig>) => void;
  resetConfig: () => void;
}

const ConfigContextJapanese = createContext<JapaneseConfigContextType>({
  config: defaultJapaneseConfig,
  updateConfig: () => {},
  resetConfig: () => {}
});

export const ConfigProviderJapanese: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<JapaneseConfig>(() => {
    const savedConfig = loadJapaneseConfig();
    return savedConfig || defaultJapaneseConfig;
  });

  const updateConfig = (newConfig: Partial<JapaneseConfig>) => {
    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);
    saveJapaneseConfig(updatedConfig);
  };

  const resetConfig = () => {
    setConfig(defaultJapaneseConfig);
    saveJapaneseConfig(defaultJapaneseConfig);
  };

  return (
    <ConfigContextJapanese.Provider value={{ config, updateConfig, resetConfig }}>
      {children}
    </ConfigContextJapanese.Provider>
  );
};

export const useConfigJapanese = () => {
  const context = useContext(ConfigContextJapanese);
  if (!context) {
    throw new Error('useConfigJapanese must be used within a ConfigProviderJapanese');
  }
  return context;
};